import { z } from 'zod';
import { adapterClient, handleApiError } from '@api/adapter/client';
import { useAuthStore } from '@/stores/authStore';
import {
  cursorSchema,
  notificationJSON,
  notificationViewSchema,
  type NotificationView,
} from '@/types/current-notification';

const pageSchema = z.object({
  notifications: z.array(notificationViewSchema).max(100),
  nextCursor: cursorSchema,
});
const textResponse = {
  responseType: 'text' as const,
  transformResponse: [notificationJSON],
};
function sameAccount(principal: string | undefined) {
  if (!principal || useAuthStore.getState().session?.principalId !== principal)
    throw new Error('Your account changed. Reload notifications.');
}
export const notificationService = {
  async getNotifications(signal?: AbortSignal): Promise<NotificationView[]> {
    const session = useAuthStore.getState().session;
    let after = '0';
    const result: NotificationView[] = [];
    try {
      // ponytail: the PoC reads ascending pages to show newest first. At large
      // histories, add backend reverse pagination rather than a second cache.
      while (true) {
        sameAccount(session?.principalId);
        const { data } = await adapterClient.get<unknown>('/notifications', {
          ...textResponse,
          params: { after },
          signal,
        });
        sameAccount(session?.principalId);
        const page = pageSchema.parse(data);
        let previous = BigInt(after);
        for (const notification of page.notifications) {
          if (
            BigInt(notification.notificationId) <= previous ||
            !notification.event.recipients.includes(session!.accountId)
          )
            throw new Error('Invalid notification page ownership or order.');
          previous = BigInt(notification.notificationId);
        }
        if (page.nextCursor !== previous.toString())
          throw new Error('Invalid notification page cursor.');
        result.push(...page.notifications);
        if (page.notifications.length < 100) return result.reverse();
        after = page.nextCursor;
      }
    } catch (error) {
      handleApiError(error, 'notifications');
    }
  },
  async markAsRead(notificationId: string, signal?: AbortSignal) {
    const session = useAuthStore.getState().session;
    const id = cursorSchema.parse(notificationId);
    try {
      sameAccount(session?.principalId);
      const { data } = await adapterClient.post<unknown>(
        '/notifications/' + id + '/read',
        undefined,
        { ...textResponse, signal }
      );
      sameAccount(session?.principalId);
      const result = notificationViewSchema.parse(data);
      if (
        result.notificationId !== id ||
        result.readAt === null ||
        !result.event.recipients.includes(session!.accountId)
      )
        throw new Error('Invalid notification read confirmation.');
      return result;
    } catch (error) {
      handleApiError(error, 'notification-read');
    }
  },
  async markAllAsRead(ids: string[]) {
    const principal = useAuthStore.getState().session?.principalId;
    for (const id of new Set(ids)) {
      sameAccount(principal);
      await notificationService.markAsRead(id);
    }
  },
};
